/* Brushes vereinfachen, ohne die Form merklich zu ändern (Gelände, Felsen): weniger Brushes = weniger BSP-Zellen, Portale und VIS-Zeit.
 * Zwei Schritte, beide mit Gültigkeitsprüfung jedes Ergebnisses:
 *  1. Dreiecks-Prismen (schräge Oberseite, flacher Boden) bilden ein Höhennetz; innere Eckpunkte entfallen, solange die Höhe um höchstens
 *     `tol` abweicht (unter Wasser `waterTol`). Rand, Stufen und Texturgrenzen bleiben.
 *  2. Benachbarte Brushes, deren Vereinigung exakt konvex ist, werden zu einem Brush (Geometrie bleibt gleich).
 * Optional kommt das Ergebnis als func_detail mit zhlt_detaillevel 1 in die Karte (zählt dann nicht für VIS). Ohne DOM. */
import type { Brush, Doc, Entity, Sel, Vec3 } from './types.ts';
import { bbox, buildHull, rebuildBrush } from './brush.ts';
import { newId } from './ids.ts';
import { isSpecialTex } from './check.ts';

export interface SimplifyOpts {
  /** größte Höhenabweichung über Wasser (Einheiten) */
  tol: number;
  /** größte Höhenabweichung unter Wasser */
  waterTol: number;
  /** Prismen entfernen/zusammenführen (Schritt 1) */
  decimate: boolean;
  /** exakt konvexe Nachbarn zusammenführen (Schritt 2) */
  merge: boolean;
  /** Ergebnis in ein func_detail (zhlt_detaillevel 1) verschieben */
  detail: boolean;
}
export const DEFAULTS: SimplifyOpts = { tol: 24, waterTol: 128, decimate: true, merge: true, detail: false };

export interface SimplifyResult {
  doc: Doc;
  sel: Sel;
  /** Brushes vor und nach dem Vereinfachen (nur die bearbeiteten) */
  before: number;
  after: number;
  /** entfernte Eckpunkte des Höhennetzes */
  removed: number;
  /** Brushes, die unverändert blieben (Spezialtexturen, Wasser, ungültiges Ergebnis) */
  kept: number;
  /** Schritt 1 wurde wegen eines ungültigen Ergebnisses ausgelassen */
  decimateFailed: boolean;
}

type V = Vec3;
const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a: V, b: V): V => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const dot = (a: V, b: V): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export function volume(b: Brush): number {
  let v = 0;
  for (const f of b.faces) { const p = f.vi.map((i) => b.verts[i]); for (let k = 1; k < p.length - 1; k++) v += dot(p[0], cross(p[k], p[k + 1])); }
  return Math.abs(v / 6);
}
function plane(b: Brush, f: { vi: number[] }): { n: V; d: number } {
  const p = f.vi.map((i) => b.verts[i]);
  const n = cross(sub(p[1], p[0]), sub(p[2], p[0])), l = Math.hypot(n[0], n[1], n[2]);
  const nn: V = [n[0] / l, n[1] / l, n[2] / l];
  return { n: nn, d: dot(nn, p[0]) };
}
function area(b: Brush, f: { vi: number[] }): number {
  const p = f.vi.map((i) => b.verts[i]);
  let a: V = [0, 0, 0];
  for (let k = 1; k < p.length - 1; k++) { const c = cross(sub(p[k], p[0]), sub(p[k + 1], p[0])); a = [a[0] + c[0], a[1] + c[1], a[2] + c[2]]; }
  return Math.hypot(a[0], a[1], a[2]) / 2;
}
const samePlane = (p: { n: V; d: number }, q: { n: V; d: number }): boolean => dot(p.n, q.n) > 0.99999 && Math.abs(p.d - q.d) < 0.05;

/** Gültiger konvexer Brush: jede Kante gehört zu genau zwei gegenläufigen Flächen, Flächen sind eben, alle Ecken liegen hinter jeder Ebene.
 *  Ohne die Kantenprüfung meldet HLCSG „Illegal Brush (edge without opposite face)“. */
export function validBrush(b: Brush, tol = 0.02): boolean {
  const edges = new Map<string, number>();
  for (const f of b.faces) {
    if (f.vi.length < 3) return false;
    for (let k = 0; k < f.vi.length; k++) {
      const a = f.vi[k], c = f.vi[(k + 1) % f.vi.length];
      if (a === c) return false;
      const key = a + '>' + c;
      edges.set(key, (edges.get(key) || 0) + 1);
    }
  }
  for (const [key, n] of edges) {
    if (n !== 1) return false;
    const [a, c] = key.split('>');
    if (edges.get(c + '>' + a) !== 1) return false;
  }
  for (const f of b.faces) {
    const p = plane(b, f);
    for (const i of f.vi) if (Math.abs(dot(p.n, b.verts[i]) - p.d) > tol) return false;
    for (const v of b.verts) if (dot(p.n, v) - p.d > tol) return false;
  }
  return true;
}

// ------------------------------------------------------------ Zusammenführen
/** Vereinigung zweier Brushes als ein Brush, wenn sie exakt konvex ist (Volumen der Hülle = Summe) und die Texturen nicht kollidieren */
export function tryMerge(A: Brush, B: Brush, tol = 0.02): Brush | null {
  const h = buildHull([...A.verts, ...B.verts], tol);
  if (!h) return null;
  const hb: Brush = { id: '', verts: h.verts, faces: h.loops.map((vi) => ({ vi, tex: '', ua: [1, 0, 0], uo: 0, va: [0, -1, 0], vo: 0, rot: 0, xs: 1, ys: 1 })) };
  const vh = volume(hb), vs = volume(A) + volume(B);
  if (Math.abs(vh - vs) > Math.max(1, 2e-5 * vs)) return null;
  const src: { f: Brush['faces'][number]; p: { n: V; d: number }; a: number }[] = [];
  for (const X of [A, B]) for (const f of X.faces) src.push({ f, p: plane(X, f), a: area(X, f) });
  const faces: Brush['faces'] = [];
  for (const f of hb.faces) {
    const p = plane(hb, f);
    const m = src.filter((s) => samePlane(s.p, p)).sort((x, y) => y.a - x.a);
    if (!m.length || m.some((s) => s.f.tex !== m[0].f.tex)) return null;
    faces.push({ ...m[0].f, vi: f.vi });
  }
  const out: Brush = { id: newId(), verts: hb.verts, faces };
  return validBrush(out) ? out : null;
}

/** Fasst Brushes in Runden zusammen; Nachbarsuche über ein grobes Gitter */
export function mergeAll(brushes: Brush[]): Brush[] {
  let list = brushes.slice();
  const G = 128;
  for (let round = 1; round < 20; round++) {
    const boxes = list.map(bbox);
    const grid = new Map<string, number[]>();
    const cells = (x: { min: V; max: V }, fn: (k: string) => void): void => {
      for (let gx = Math.floor((x.min[0] - 1) / G); gx <= Math.floor((x.max[0] + 1) / G); gx++)
        for (let gy = Math.floor((x.min[1] - 1) / G); gy <= Math.floor((x.max[1] + 1) / G); gy++) fn(gx + ',' + gy);
    };
    list.forEach((_, i) => cells(boxes[i], (k) => { (grid.get(k) || grid.set(k, []).get(k) as number[]).push(i); }));
    const dead = new Set<number>(), repl = new Map<number, Brush>();
    const touch = (a: { min: V; max: V }, b: { min: V; max: V }): boolean => { for (let i = 0; i < 3; i++) if (a.max[i] < b.min[i] - 0.1 || b.max[i] < a.min[i] - 0.1) return false; return true; };
    let merged = 0;
    for (let i = 0; i < list.length; i++) {
      if (dead.has(i)) continue;
      let cur = repl.get(i) || list[i], cb = bbox(cur), again = true;
      while (again) {
        again = false;
        const cand = new Set<number>();
        cells(cb, (k) => { for (const j of grid.get(k) || []) if (j > i && !dead.has(j)) cand.add(j); });
        for (const j of cand) {
          if (dead.has(j) || !touch(cb, boxes[j])) continue;
          const m = tryMerge(cur, repl.get(j) || list[j]);
          if (m) { cur = m; cb = bbox(cur); dead.add(j); merged++; again = true; break; }
        }
      }
      if (cur !== list[i]) repl.set(i, cur);
    }
    list = list.map((b, i) => repl.get(i) || b).filter((_, i) => !dead.has(i));
    if (!merged) break;
  }
  return list;
}

// ------------------------------------------------------------ Höhennetz
export interface Tri { v: [number, number, number]; bottom: number; tex: string; donor: Brush; alive: boolean }
export interface Mesh { verts: V[]; tris: Tri[] }

const key = (v: V): string => Math.round(v[0] * 100) + ',' + Math.round(v[1] * 100) + ',' + Math.round(v[2] * 100);
/** > 0: gegen den Uhrzeigersinn (Draufsicht) */
const area2 = (a: V, b: V, c: V): number => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

/** Dreiecks-Prisma: oben eine schräge Fläche mit 3 Ecken, unten flach, rundum senkrecht */
export function prismTri(b: Brush): { top: V[]; bottom: number; tex: string } | null {
  if (b.faces.length !== 5) return null;
  let top = -1, down = -1, vert = 0, bad = false;
  b.faces.forEach((f, i) => {
    const p = plane(b, f);
    if (p.n[2] > 0.05) { if (top >= 0) bad = true; top = i; } else if (p.n[2] < -0.9999) down = i; else if (Math.abs(p.n[2]) < 0.05) vert++;
  });
  if (bad || top < 0 || down < 0 || vert !== 3 || b.faces[top].vi.length !== 3) return null;
  return { top: b.faces[top].vi.map((i) => b.verts[i]), bottom: Math.min(...b.verts.map((v) => v[2])), tex: b.faces[top].tex };
}

export function buildMesh(prisms: Brush[]): { mesh: Mesh; others: Brush[] } {
  const verts: V[] = [], idx = new Map<string, number>(), tris: Tri[] = [], others: Brush[] = [];
  const vid = (v: V): number => {
    const k = key(v);
    let i = idx.get(k);
    if (i === undefined) { i = verts.length; idx.set(k, i); verts.push([Math.round(v[0] * 100) / 100, Math.round(v[1] * 100) / 100, Math.round(v[2] * 100) / 100]); }
    return i;
  };
  for (const b of prisms) {
    const p = prismTri(b);
    if (!p) { others.push(b); continue; }
    let t: [number, number, number] = [vid(p.top[0]), vid(p.top[1]), vid(p.top[2])];
    if (area2(verts[t[0]], verts[t[1]], verts[t[2]]) < 0) t = [t[0], t[2], t[1]];
    if (new Set(t).size < 3 || Math.abs(area2(verts[t[0]], verts[t[1]], verts[t[2]])) < 1e-6) { others.push(b); continue; }
    tris.push({ v: t, bottom: Math.round(p.bottom * 100) / 100, tex: p.tex, donor: b, alive: true });
  }
  return { mesh: { verts, tris }, others };
}

const inside = (p: V, a: V, b: V, c: V): boolean => area2(a, b, p) >= -1e-9 && area2(b, c, p) >= -1e-9 && area2(c, a, p) >= -1e-9;
function zAt(p: V, a: V, b: V, c: V): number {
  const d = area2(a, b, c), wa = area2(p, b, c) / d, wb = area2(a, p, c) / d, wc = area2(a, b, p) / d;
  return wa * a[2] + wb * b[2] + wc * c[2];
}

/** Ohrenschnitt eines einfachen Polygons (gegen den Uhrzeigersinn); liefert Dreiecke aus Ring-Indizes oder null */
function earClip(ring: V[]): [number, number, number][] | null {
  const idx = ring.map((_, i) => i), out: [number, number, number][] = [];
  let guard = 0;
  while (idx.length > 3 && guard++ < 1000) {
    let best = -1, bestQ = -1;
    for (let k = 0; k < idx.length; k++) {
      const a = idx[(k + idx.length - 1) % idx.length], b = idx[k], c = idx[(k + 1) % idx.length];
      const ar = area2(ring[a], ring[b], ring[c]);
      if (ar <= 1e-6) continue;
      let ok = true;
      for (const o of idx) if (o !== a && o !== b && o !== c && inside(ring[o], ring[a], ring[b], ring[c]) && area2(ring[a], ring[b], ring[o]) > -1e-9) { ok = false; break; }
      if (!ok) continue;
      const l = [Math.hypot(ring[a][0] - ring[b][0], ring[a][1] - ring[b][1]), Math.hypot(ring[b][0] - ring[c][0], ring[b][1] - ring[c][1]), Math.hypot(ring[c][0] - ring[a][0], ring[c][1] - ring[a][1])];
      const q = ar / (l[0] * l[0] + l[1] * l[1] + l[2] * l[2]); // größer = gleichmäßigere Dreiecke
      if (q > bestQ) { bestQ = q; best = k; }
    }
    if (best < 0) return null;
    const a = idx[(best + idx.length - 1) % idx.length], b = idx[best], c = idx[(best + 1) % idx.length];
    out.push([a, b, c]); idx.splice(best, 1);
  }
  if (idx.length === 3) { if (area2(ring[idx[0]], ring[idx[1]], ring[idx[2]]) <= 1e-6) return null; out.push([idx[0], idx[1], idx[2]]); }
  return out;
}

/** Entfernt innere Eckpunkte, solange die Fläche höchstens tol(v) abweicht. Rand-, Stufen- und Texturgrenzen bleiben. */
function decimateMesh(mesh: Mesh, tol: (v: V) => number, minArea: number, maxPasses: number): number {
  let removed = 0;
  for (let pass = 0; pass < maxPasses; pass++) {
    const inc = new Map<number, number[]>();
    mesh.tris.forEach((t, i) => { if (!t.alive) return; for (const v of t.v) (inc.get(v) || inc.set(v, []).get(v) as number[]).push(i); });
    const edgeN = new Map<string, number>();
    mesh.tris.forEach((t) => { if (!t.alive) return; for (let k = 0; k < 3; k++) { const a = t.v[k], b = t.v[(k + 1) % 3], e = a < b ? a + '_' + b : b + '_' + a; edgeN.set(e, (edgeN.get(e) || 0) + 1); } });
    const cands: { v: number; err: number; fan: number[]; ring: number[]; tris: [number, number, number][] }[] = [];
    for (const [v, fan] of inc) {
      const T = fan.map((i) => mesh.tris[i]);
      if (fan.length < 3 || fan.length > 12) continue;
      if (T.some((t) => t.bottom !== T[0].bottom || t.tex !== T[0].tex)) continue;
      // innerer Punkt: alle Kanten am Punkt gehören zu genau zwei Dreiecken
      let closed = true;
      for (const t of T) for (const u of t.v) { if (u === v) continue; const e = u < v ? u + '_' + v : v + '_' + u; if (edgeN.get(e) !== 2) closed = false; }
      if (!closed) continue;
      // Ring der Nachbarn (gegen den Uhrzeigersinn): Kante a→b jedes Dreiecks (v,a,b)
      const next = new Map<number, number>();
      for (const t of T) { const k = t.v.indexOf(v); next.set(t.v[(k + 1) % 3], t.v[(k + 2) % 3]); }
      const ring: number[] = [], start = next.keys().next().value as number;
      let cur: number | undefined = start;
      do { ring.push(cur as number); cur = next.get(cur as number); } while (cur !== undefined && cur !== start && ring.length <= fan.length);
      if (ring.length !== fan.length || cur !== start) continue;
      const rv = ring.map((i) => mesh.verts[i]);
      const nt = earClip(rv);
      if (!nt) continue;
      const P = mesh.verts[v];
      if (nt.some(([a, b, c]) => area2(rv[a], rv[b], rv[c]) < minArea)) continue;
      const samples: V[] = [P, ...rv.map((u): V => [(P[0] + u[0]) / 2, (P[1] + u[1]) / 2, (P[2] + u[2]) / 2])];
      let err = 0;
      for (const s of samples) {
        const tri = nt.find(([a, b, c]) => inside(s, rv[a], rv[b], rv[c]));
        if (!tri) { err = Infinity; break; }
        err = Math.max(err, Math.abs(zAt(s, rv[tri[0]], rv[tri[1]], rv[tri[2]]) - s[2]));
      }
      if (err <= tol(P)) cands.push({ v, err, fan, ring, tris: nt.map(([a, b, c]) => [ring[a], ring[b], ring[c]] as [number, number, number]) });
    }
    cands.sort((a, b) => a.err - b.err);
    const dirty = new Set<number>();
    let n = 0;
    for (const c of cands) {
      if (dirty.has(c.v) || c.ring.some((r) => dirty.has(r))) continue;
      const donor = mesh.tris[c.fan[0]];
      for (const i of c.fan) mesh.tris[i].alive = false;
      for (const t of c.tris) mesh.tris.push({ v: t, bottom: donor.bottom, tex: donor.tex, donor: donor.donor, alive: true });
      dirty.add(c.v); c.ring.forEach((r) => dirty.add(r)); n++;
    }
    removed += n;
    if (!n) break;
  }
  return removed;
}

/** Prismen aus den Dreiecken; Texturausrichtung der Oberseite vom Spender. null, wenn ein Ergebnis ungültig wäre. */
export function rebuild(mesh: Mesh): Brush[] | null {
  const out: Brush[] = [];
  for (const t of mesh.tris) {
    if (!t.alive) continue;
    const P = t.v.map((i) => mesh.verts[i]);
    const pts: V[] = [...P, ...P.map((p): V => [p[0], p[1], t.bottom])];
    const b = rebuildBrush(t.donor, pts, { tol: 0.02, tex: t.tex });
    if (!b) return null;
    const donorTop = t.donor.faces.find((f) => plane(t.donor, f).n[2] > 0.05);
    if (donorTop) b.faces.forEach((f) => { if (plane(b, f).n[2] > 0.05) Object.assign(f, donorTop, { vi: f.vi }); });
    b.id = newId();
    delete b.srcText;
    if (!validBrush(b)) return null;
    out.push(b);
  }
  return out;
}

// ------------------------------------------------------------ Ablauf
/** NULL zählt nicht: Gelände aus dem Generator hat NULL auf der Unterseite und soll bearbeitbar bleiben */
export const isProtected = (b: Brush): boolean => b.faces.some((f) => (isSpecialTex(f.tex) && f.tex.toUpperCase() !== 'NULL') || f.tex.charAt(0) === '!');

/** Wasserfläche der Karte: Brushes mit Textur !… (Unterwasser darf gröber vereinfacht werden) */
function waterBox(doc: Doc): { min: V; max: V } | null {
  const w = doc.ents[0].brushes.filter((b) => b.faces.some((f) => f.tex.charAt(0) === '!'));
  if (!w.length) return null;
  const mn: V = [Infinity, Infinity, Infinity], mx: V = [-Infinity, -Infinity, -Infinity];
  for (const b of w) { const bb = bbox(b); for (let i = 0; i < 3; i++) { mn[i] = Math.min(mn[i], bb.min[i]); mx[i] = Math.max(mx[i], bb.max[i]); } }
  return { min: mn, max: mx };
}

/** Mindestens eine Fläche liegt nicht achsparallel (Gelände, Felsen); Wände und Quader nicht */
const isSloped = (b: Brush): boolean => b.faces.some((f) => { const n = plane(b, f).n; return Math.max(Math.abs(n[0]), Math.abs(n[1]), Math.abs(n[2])) < 0.9999; });

/** Die Welt-Brushes, die `simplify` bearbeitet: die Auswahl, ohne Auswahl alle schrägen Welt-Brushes (so bleiben Wände und Gebäude unberührt,
 *  und mit `detail` landet nur das Gelände im func_detail). Himmel, Wasser und Spezialtexturen sind immer ausgenommen. */
export function targets(doc: Doc, sel: Sel | null): Brush[] {
  const w = doc.ents[0].brushes;
  const pick = sel && sel.b.size ? w.filter((b) => sel.b.has(b.id)) : w.filter(isSloped);
  return pick.filter((b) => !isProtected(b));
}

export function simplify(doc: Doc, sel: Sel | null, o: SimplifyOpts): SimplifyResult {
  const todo = targets(doc, sel), ids = new Set(todo.map((b) => b.id));
  const result = (d: Doc, nb: Brush[], removed: number, failed: boolean): SimplifyResult => {
    const s: Sel = { b: new Set(nb.map((b) => b.id)), e: new Set() };
    return { doc: d, sel: s, before: todo.length, after: nb.length, removed, kept: doc.ents[0].brushes.length - todo.length, decimateFailed: failed };
  };
  if (!todo.length) return result(doc, [], 0, false);

  let work = todo, removed = 0, failed = false;
  if (o.decimate) {
    const wb = waterBox(doc);
    const under = (v: V): boolean => !!wb && v[0] >= wb.min[0] - 1 && v[0] <= wb.max[0] + 1 && v[1] >= wb.min[1] - 1 && v[1] <= wb.max[1] + 1 && v[2] <= wb.max[2];
    const { mesh, others } = buildMesh(todo);
    if (mesh.tris.length) {
      removed = decimateMesh(mesh, (v) => (under(v) ? o.waterTol : o.tol), 40, 40);
      const prisms = removed ? rebuild(mesh) : null;
      if (removed && !prisms) { failed = true; removed = 0; } else if (prisms) work = [...prisms, ...others];
    }
  }
  if (o.merge) work = mergeAll(work);

  const rest = doc.ents[0].brushes.filter((b) => !ids.has(b.id));
  let ents: Entity[];
  if (o.detail) {
    const det: Entity = { id: newId(), kv: { classname: 'func_detail', zhlt_detaillevel: '1' }, brushes: work };
    ents = [{ ...doc.ents[0], brushes: rest }, ...doc.ents.slice(1), det];
  } else {
    ents = [{ ...doc.ents[0], brushes: [...rest, ...work] }, ...doc.ents.slice(1)];
  }
  return result({ ents }, work, removed, failed);
}
